import {
  addMinutes,
  differenceInHours,
  differenceInMinutes,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isValid,
  isWithinInterval,
  parse,
} from 'date-fns';
import { format as formatTz, utcToZonedTime } from 'date-fns-tz';

import { IRestaurant } from '@rbi-ctg/store';
import { IOperatingHours, IServiceModeHours } from 'generated/graphql-gateway';
import { ServiceMode } from 'state/service-mode/types';
import LocalStorage from 'utils/cognito/storage.web';
import { hoursInDay } from 'utils/dateTime/constants';
import { StorageKeys } from 'utils/local-storage';
import { PICKUP_SERVICE_MODES, SERVICE_MODES, isPickup } from 'utils/service-mode';
import { toISOString } from 'utils/time-utils';

import {
  ALT_PARSE_FORMAT,
  DEFAULT_STORE_TIMEZONE,
  LOCALE_TIME_PARSE_FORMAT,
  PARSE_FORMAT,
  SIMPLIFIED_TWELVE_HOUR_TIME_PARSE_FORMAT,
  TWELVE_HOUR_TIME_PARSE_FORMAT,
} from '../constants';
import { Days } from '../types';

export function nextDay(date: Date) {
  return Days[(date.getDay() + 1) % 7];
}

/*
 * Returns opening hours for the day after the the `referenceDate`.
 */
export function getNextDayOpeningHours(hours: IOperatingHours, date: Date): string {
  return hours[nextDay(date).concat('Open')];
}

/**
 * Parses a time string from mdm
 *
 * time strings can come in three flavors, so we have to try to parse all three
 * * yyyy-MM-dd HH:mm:ss.SSSSSSS
 * * yyyy-MM-dd HH:mm:ss
 * * HH:mm:ss
 *
 */
export function parseMdmtimeISOString(
  timeISOString: string,
  referenceDate: Date = new Date()
): Date {
  let formattedTime = parse(timeISOString, PARSE_FORMAT, referenceDate);

  if (!isValid(formattedTime)) {
    formattedTime = parse(timeISOString, ALT_PARSE_FORMAT, referenceDate);
  }

  if (!isValid(formattedTime)) {
    formattedTime = parse(timeISOString, LOCALE_TIME_PARSE_FORMAT, referenceDate);
  }

  return formattedTime;
}

/**
 * This function is used to apply custom time adjustments to the time string.
 *
 * If the time string has ":00" minutes and the specified format is
 * SIMPLIFIED_TWELVE_HOUR_TIME_PARSE_FORMAT, this function removes the ":00"
 * portion and returns the result in lowercase.
 *
 * For example, "10:00AM" becomes "10am".
 *
 * @param time - The input time string (e.g., "10:00AM").
 * @param timeFormat - The expected time format to trigger formatting.
 * @returns The formatted time string (e.g., "10am") or the original string if no changes apply.
 */
const applyCustomTimeAdjustments = (time: string, timeFormat: string) => {
  if (timeFormat === SIMPLIFIED_TWELVE_HOUR_TIME_PARSE_FORMAT) {
    return time.replace(':00', '').toLowerCase();
  }
  return time;
};

/**
 * Displays the given time string in a human readable format, either in standard or
 * military time format based on the market configuration.
 * @param timeISOString Time string.
 * @returns Human readable time string.
 */
interface IReadableHourProps {
  timeISOString: string;
  timeFormat?: string;
  storeTimeZone?: string;
}
export function readableHour({
  timeISOString,
  timeFormat = TWELVE_HOUR_TIME_PARSE_FORMAT,
  storeTimeZone = DEFAULT_STORE_TIMEZONE,
}: IReadableHourProps) {
  try {
    const dateInStoreTimeZone = utcToZonedTime(timeISOString, storeTimeZone);
    const readableHour = formatTz(dateInStoreTimeZone, timeFormat, { timeZone: storeTimeZone });

    return applyCustomTimeAdjustments(readableHour, timeFormat);
  } catch (error) {
    return 'hours not available';
  }
}

interface IReadableTimeInterval {
  fallbackMessage: string;
  open24HoursMessage?: string;
  timeFormat?: string;
}
export const readableTimeInterval = ({
  fallbackMessage,
  open24HoursMessage,
  timeFormat = TWELVE_HOUR_TIME_PARSE_FORMAT,
}: IReadableTimeInterval) => (fromTime: string, toTime: string) => {
  try {
    const parsedFromTime = parseMdmtimeISOString(fromTime);
    let parsedToTime = parseMdmtimeISOString(toTime);

    // If the closing time is 23:59, we add 1 minute to make it 00:00 of the next day.
    // This is done to interpret a time range from 00:00 to 23:59 as a full 24-hour period.
    if (parsedToTime.getHours() === 23 && parsedToTime.getMinutes() === 59) {
      parsedToTime = addMinutes(parsedToTime, 1);
    }

    let readableFromHour = format(parsedFromTime, timeFormat);
    let readableToHour = format(parsedToTime, timeFormat);

    const is24HourInterval =
      differenceInHours(parsedToTime, parsedFromTime) === hoursInDay ||
      differenceInMinutes(parsedToTime, parsedFromTime) === 0;
    if (is24HourInterval && open24HoursMessage) {
      return open24HoursMessage;
    }

    [readableFromHour, readableToHour] = [readableFromHour, readableToHour].map(time =>
      applyCustomTimeAdjustments(time, timeFormat)
    );

    return `${readableFromHour} - ${readableToHour}`;
  } catch (e) {
    return fallbackMessage;
  }
};

export const isStoreClosingWithinMinutes = (store: IRestaurant, minutes = 60) => {
  const { closingTime } = getRestaurantLatestClosingTime({ store, serviceModes: 'pickup' }) ?? {};

  if (!closingTime || !store.localTime || !store.timezone) {
    return false;
  }

  const storeLocalTime = new Date(store.localTime);

  const diff = differenceInMinutes(closingTime, storeLocalTime);

  return diff <= minutes;
};

export function readableCloseHourToday(
  store: IRestaurant,
  timeFormat?: string,
  onlyPickupServiceModes = true
) {
  const { closingTime } =
    getRestaurantLatestClosingTime({
      store,
      serviceModes: onlyPickupServiceModes ? 'pickup' : 'all',
    }) ?? {};
  const closingTimeISO = toISOString(closingTime ?? '', store.timezone ?? '');

  return readableHour({
    timeISOString: closingTimeISO,
    timeFormat,
    storeTimeZone: store.timezone || undefined,
  });
}

/*
 * Given a time string of the format "yyyy-MM-dd HH:mm:ss" (usually something like `1970-01-01 23:59:00.0000000`),
 * return today's date, in the store timezone, with hours, minutes, and seconds from the time string.
 */
export function getDateForHours(nowInStoreTimeZone: Date, openingOrClosingHours: string) {
  const parsedHours = parseMdmtimeISOString(openingOrClosingHours, nowInStoreTimeZone);

  parsedHours.setFullYear(nowInStoreTimeZone.getFullYear());
  parsedHours.setMonth(nowInStoreTimeZone.getMonth());
  parsedHours.setDate(nowInStoreTimeZone.getDate());

  return parsedHours;
}

/**
 * Checks if the store is currently open for at least one of the given service modes.
 *
 * If no `serviceModes` are provided, all available service modes will be considered.
 * The function returns `true` as soon as it finds a service mode that is currently open
 * and `false` if none are open.
 *
 */
export const isStoreOpen = ({
  store,
  serviceModes = SERVICE_MODES,
  currentStoreDate,
}: {
  store: IRestaurant;
  serviceModes?: ServiceMode[];
  currentStoreDate: Date;
}) => {
  for (const serviceMode of serviceModes) {
    const hours = store.serviceModeHours?.find(
      serviceModeHour => serviceModeHour.serviceMode === serviceMode
    );

    if (!hours?.open || !hours?.close || !hours?.nextOpen) {
      continue;
    }

    const openHour = new Date(hours.open);
    const closeHour = new Date(hours.close);
    const nextOpenHour = new Date(hours.nextOpen);
    const nextCloseHour = new Date(hours.nextClose);

    const isCloseAndNextOpenBackToBack = isCloseAndNextOpenWithin1m({
      nextOpen: nextOpenHour,
      close: closeHour,
    });

    if (
      isAfter(currentStoreDate, openHour) &&
      isBefore(currentStoreDate, nextCloseHour) &&
      (hours?.isOpen24Hours || isCloseAndNextOpenBackToBack)
    ) {
      return true;
    }

    // Returns true after finding the first open service mode
    if (
      isWithinInterval(currentStoreDate, { start: openHour, end: closeHour }) ||
      isWithinInterval(currentStoreDate, { start: nextOpenHour, end: nextCloseHour })
    ) {
      return true;
    }
  }

  // None of the service modes are open
  return false;
};

// Get hour in a.m or p.m. from HH:mm string
export const formatTime = (timeISOString: any) => {
  const [hourString, minute] = timeISOString.split(':');
  const hour = +hourString % 24;
  return (hour % 12 || 12) + ':' + minute + (hour < 12 ? ' a.m.' : ' p.m.');
};

/**
 * Returns the operating hours of the store for the provided service mode.
 * @param store The store from which to obtain operating hours.
 * @param serviceMode The service mode whose hours to get.
 * @returns Store operating hours for the provided service mode if available or `null` or `undefined` otherwise.
 */
export function getStoreOperatingHoursForServiceMode(store: IRestaurant, serviceMode: ServiceMode) {
  switch (serviceMode) {
    case ServiceMode.DELIVERY:
      return store.deliveryHours;
    case ServiceMode.DRIVE_THRU:
      return store.driveThruHours;
    case ServiceMode.MOBILE_ORDER_DRIVE_THRU:
      return store.mobileOrderDriveThruHours;
    default:
      return store.diningRoomHours;
  }
}

/**
 * Retrieves the latest closing time among the service modes for a given restaurant.
 *
 * Iterates through all defined `ServiceMode` values and, depending on the `onlyPickupServiceModes` flag,
 * filters to include only pickup-related modes. It collects the closing times for the current day,
 * excluding service modes that are 24 hours or have missing data.
 *
 * @param params - Parameters object.
 * @param params.restaurant - The restaurant whose service modes will be checked.
 * @param params.onlyPickupServiceModes - If true, only pickup service modes will be considered.
 * @param params.include24HourServiceModes - If true, 24-hour service modes will be included in the calculation.
 * @param params.excludeServiceModes - If provided, service modes in this array will be excluded from the calculation.
 *
 * @returns An array of service modes with their corresponding closing times.
 */
export const getRestaurantLatestClosingTime = ({
  store,
  serviceModes = 'all',
  storeLocalTime,
  include24HourServiceModes = false,
  excludeServiceModes = [],
}: {
  store: IRestaurant;
  serviceModes?: 'pickup' | 'delivery' | 'all';
  storeLocalTime?: string;
  include24HourServiceModes?: boolean;
  excludeServiceModes?: ServiceMode[];
}) => {
  const serviceModesClosingTime: {
    serviceMode: ServiceMode;
    closingTime: Date | null;
    openingTime: Date | null;
  }[] = [];

  let serviceModesArray: ServiceMode[] = [];
  if (serviceModes === 'delivery') {
    serviceModesArray = [ServiceMode.DELIVERY];
  } else if (serviceModes === 'pickup') {
    serviceModesArray = PICKUP_SERVICE_MODES;
  } else {
    serviceModesArray = SERVICE_MODES;
  }

  const onlyPickupServiceModes = serviceModes === 'pickup';

  for (const serviceMode of serviceModesArray) {
    const isPickupServiceMode = isPickup(serviceMode);

    if (onlyPickupServiceModes && !isPickupServiceMode) {
      continue;
    }

    if (excludeServiceModes.includes(serviceMode)) {
      continue;
    }

    const operatingHours = getTodayRestaurantOperatingHoursByServiceMode({
      restaurant: store,
      serviceMode,
    });

    if (!operatingHours || !store.timezone) {
      continue;
    }

    const { openingTime, closingTime, nextOpeningTime, open24hs } = operatingHours;

    if (!openingTime || !closingTime || !nextOpeningTime) {
      continue;
    }

    const isCloseAndNextOpenBackToBack = isCloseAndNextOpenWithin1m({
      nextOpen: nextOpeningTime,
      close: closingTime,
    });

    // If there is any 24hs service mode and we're not including them, we don't return any closing time
    if ((open24hs || isCloseAndNextOpenBackToBack) && !include24HourServiceModes) {
      return null;
    }

    serviceModesClosingTime.push({
      serviceMode,
      closingTime,
      openingTime,
    });
  }

  if (!serviceModesClosingTime.length) {
    return null;
  }

  // If storeLocalTime is not valid of undefined, use store.localTime
  let localTime = utcToZonedTime(storeLocalTime || '', store.timezone || '');
  if (!isValid(localTime)) {
    localTime = utcToZonedTime(store.localTime || '', store.timezone || '');
  }

  // If there are closing times that occur on the day after their opening time,
  // filter those and return the one with the latest closing time.
  // Otherwise, use the full list.
  const crossDayServiceModes = serviceModesClosingTime.filter(
    ({ openingTime, closingTime }) =>
      openingTime &&
      closingTime &&
      isAfter(localTime, openingTime) &&
      !isSameDay(openingTime, closingTime)
  );
  const targetServiceModes =
    crossDayServiceModes.length > 0 ? crossDayServiceModes : serviceModesClosingTime;

  return targetServiceModes.reduce((latest, current) => {
    if (!latest.closingTime) {
      return current;
    }
    if (!current.closingTime) {
      return latest;
    }
    return isAfter(current.closingTime, latest.closingTime) ? current : latest;
  });
};

/**
 * Function to validate whether the service mode should be treated as “24-hour”.
 * It is for edge cases, when `close` and `nextOpen` are back-to-back (e.g., 03:59 → 04:00),
 * i.e., the difference is 1 minute or less. In that case, return `true` to avoid
 * showing a 1-minute “closed” gap.
 */
const isCloseAndNextOpenWithin1m = ({ close, nextOpen }: { close: Date; nextOpen: Date }) => {
  return differenceInMinutes(nextOpen, close) <= 1;
};

type BaseGetTimesArgs = {
  store: IRestaurant;
  onlyPickupServiceModes: boolean;
};

type Comparator = (a: string, b: string) => boolean;

function getServiceModeHours(
  { store, onlyPickupServiceModes }: BaseGetTimesArgs,
  timeKey: keyof Omit<IServiceModeHours, 'serviceMode' | 'isOpen24Hours' | '__typename'>,
  compareFn: Comparator
) {
  const result: IServiceModeHours[] = [];
  const serviceModes = onlyPickupServiceModes ? PICKUP_SERVICE_MODES : SERVICE_MODES;

  for (const serviceMode of serviceModes) {
    if (onlyPickupServiceModes && !isPickup(serviceMode)) {
      continue;
    }
    const smh = store.serviceModeHours?.find(smh => smh.serviceMode === serviceMode);
    if (!smh) {
      continue;
    }
    if (smh.isOpen24Hours) {
      return null;
    }

    result.push(smh);
  }

  if (!result.length) {
    return null;
  }

  return result.reduce((chosen, current) => {
    const chosenTime = chosen[timeKey];
    const currentTime = current[timeKey];
    if (!chosenTime) {
      return current;
    }
    if (!currentTime) {
      return chosen;
    }
    return compareFn(currentTime, chosenTime) ? current : chosen;
  });
}

export function getNextEarliestOpeningHours(args: BaseGetTimesArgs) {
  return getServiceModeHours(
    args,
    'nextOpen',
    (current, chosen) => current < chosen // earliest = smallest time
  );
}

export function getNextLatestClosingHours(args: BaseGetTimesArgs) {
  return getServiceModeHours(
    args,
    'nextClose',
    (current, chosen) => current > chosen // latest = largest time
  );
}

const getTodayRestaurantOperatingHoursByServiceMode = ({
  restaurant,
  serviceMode,
}: {
  restaurant: IRestaurant;
  serviceMode: ServiceMode;
}) => {
  // For TAKEOUT service mode, use EAT_IN hours if available
  // TODO: remove this once BE return and map all the service mode hours as we are doing in getStoreOperatingHoursForServiceMode
  // https://rbictg.atlassian.net/browse/BKPE-22019
  // https://github.com/rbilabs/bk-store-service/blob/a87b6959603ab89d261842f1e0695e91ea01cbcc/src/modules/restaurant/constants.ts#L40-L41
  const targetServiceMode = serviceMode === ServiceMode.TAKEOUT ? ServiceMode.EAT_IN : serviceMode;
  const serviceModeHours = restaurant.serviceModeHours?.find(
    serviceModeHours => serviceModeHours.serviceMode === targetServiceMode
  );

  const { open, close, nextOpen, nextClose } = serviceModeHours || {};

  // TODO: do we really need this?
  if (open === undefined || close === undefined) {
    return null;
  }

  const openingTime = open ? new Date(open) : null;
  const closingTime = close ? new Date(close) : null;
  const nextOpeningTime = nextOpen ? new Date(nextOpen) : null;
  const nextClosingTime = nextClose ? new Date(nextClose) : null;

  return {
    openingTime,
    closingTime,
    nextOpeningTime,
    nextClosingTime,
    open24hs: !!serviceModeHours?.isOpen24Hours,
  };
};

export function getCurrentDateInStoreTimeZone(store: IRestaurant): Date {
  const localTime = store.localTime ?? '';
  const storeTimeZone = store.timezone || DEFAULT_STORE_TIMEZONE;

  return utcToZonedTime(localTime, storeTimeZone);
}

/** Important Note! this function's result will change across renders since it is time based
 *  and can throw off useEffects that want stable values. Don't pass this result directly into a useEffect dep list  */
export function getSelectedStoreCurrentDate(timezone?: string | undefined): Date {
  const localTime = getStoreLocalTime() ?? '';
  const storeTimeZone = timezone || DEFAULT_STORE_TIMEZONE;

  return utcToZonedTime(localTime, storeTimeZone);
}

/** Important Note! this function's result will change across renders since it is time based
 *  and can throw off useEffects that want stable values. Don't pass this result directly into a useEffect dep list  */
export function getStoreLocalTime() {
  return LocalStorage.getItem<string>(StorageKeys.SELECTED_STORE_LOCAL_TIME);
}

export function setStoreLocalTime(time: string) {
  LocalStorage.setItem(StorageKeys.SELECTED_STORE_LOCAL_TIME, time);
}

export function clearStoreLocalTime() {
  LocalStorage.removeItem(StorageKeys.SELECTED_STORE_LOCAL_TIME);
}
